import React, { useEffect } from 'react';
import { images } from '../../components/shared/assets';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useOverlayManager } from '../../contexts/OverlayContext';
import '../../styles/property-pages.css';
import './ProducingNatureAtacama.css';

const ProducingNatureAtacama: React.FC = () => {
  const { setHoveredDestination } = useOverlayManager();

  // Initialize interactive features
  useEffect(() => {
    // Initialize all interactive features
    initializeAllFeatures();

    // Initialize horizontal scroll for the content carousel
    const cleanupCarousel = initializeDragScroll('draggable-carousel');

    // Initialize horizontal scroll for the blog carousel
    const cleanupBlogCarousel = initializeDragScroll('blog-carousel');

    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);

    return () => {
      if (typeof cleanupCarousel === 'function') cleanupCarousel();
      if (typeof cleanupBlogCarousel === 'function') cleanupBlogCarousel();
    };
  }, []);

  return (
    <div className="producing-nature-page atacama-producing-nature">
      {/* Navigation - Atacama variant */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Hero Section with Video */}
      <section className="producing-hero" data-scroll-section="hero">
        <video
          className="producing-hero-video"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src="https://videos.ctfassets.net/hz1mviiqoqof/5f7KvUi1bDZSsQ7sYHowQb/81b13f4425174508f742bc8bd42febf1/filmCollectionHeaderVideo.mp4" type="video/mp4" />
        </video>
        <h1>
          Producing Nature
        </h1>
        <div className="atacama-hero-overlay">
          <span>ATACAMA</span>
        </div>
      </section>

      {/* Full Image Section */}
      <section className="full-image-section" data-scroll-section="full-image">
        <div className="sticky-image-container">
          <div
            className="sticky-image"
            style={{ backgroundImage: `url('${images.producingNaturePage.carbonNeutralityBg}')` }}
          />
        </div>
      </section>

      {/* Content Carousel Section - Atacama-specific content */}
      <section className="horizontal-scroll-carousel atacama-carousel" data-scroll-section>
        <div
          className="horizontal-scroll-carousel__scroll-container"
          id="draggable-carousel"
        >
          {/* Frame 1: Opening Image with Text Overlay */}
          <div className="producing-nature-frame opening-frame">
            <div className="frame-image-section full-height">
              <img src="/assets/figma/a7ca385cea25f3635392fa96b7c02cf0a6d12cae.png" alt="Atacama flamingo" />
            </div>
            <div className="frame-overlay-content">
              <h2>We Recognize Time, Space and Silence as the Greatest of Privileges</h2>
              <div className="overlay-badge">
                <span>ATACAMA</span>
              </div>
            </div>
          </div>

          {/* Frame 2: Carbon Neutrality with Split Layout */}
          <div className="producing-nature-frame carbon-frame">
            <div className="frame-content-wrapper">
              <div className="frame-image-section split-image">
                <img src="/assets/figma/3ae52cc439b17491f4592b688d60787859659cb8.png" alt="Desert landscape" />
              </div>
              <div className="frame-text-section">
                <h3>Carbon Neutrality</h3>
                <p>At the preservation of biodiversity depends on the balance between the Earth and the elements that surround it.</p>
                <p>Scientific discoveries have shown how human activity has altered this balance, resulting in unprecedented changes that are affecting all life on our planet. This is why we have become a carbon neutral operation, developing a series of comprehensive actions.</p>
              </div>
            </div>
          </div>

          {/* Frame 3: Llama/Vicuña with Text */}
          <div className="producing-nature-frame vicuna-frame">
            <div className="frame-content-wrapper">
              <div className="frame-text-section">
                <h3>Forest Protection in Unique Ecosystems</h3>
                <p>We are proud to be part of several conservation initiatives to protect Chile's unique ecosystems. In partnership with Fundación Reforestemos, we have contributed to the restoration of native forests affected by wildfires.</p>
                <p>Additionally, through our collaboration with the Atacama Foundation, we support the preservation of high-altitude wetlands and the unique flora that has adapted to extreme desert conditions.</p>
              </div>
              <div className="frame-image-section split-image">
                <img src="/assets/figma/4068ccb307e6b17d16d0cec4e523eac819e02a87.png" alt="Vicuña in desert" />
              </div>
            </div>
          </div>

          {/* Frame 4: Blue Section */}
          <div className="producing-nature-frame blue-frame">
            <div className="blue-content">
              <h3>Nature & Wildlife</h3>
              <p>The Atacama Desert's extreme conditions have created unique adaptations in its wildlife. From Andean flamingos in high-altitude salt lakes to vicuñas roaming the altiplano, each species tells a story of resilience and evolution.</p>
            </div>
          </div>

          {/* Frame 5: Desert Landscape */}
          <div className="producing-nature-frame desert-landscape-frame">
            <img src="/assets/figma/853014457bcaecdf6a163e42e820f91e22e032c8.png" alt="Desert landscape" />
          </div>

          {/* Frame 6: Mountains with Text Overlay */}
          <div className="producing-nature-frame mountains-frame">
            <div className="frame-image-section full-height">
              <img src="/assets/figma/4f09b905d851cc5f7a6af53dc7a75c5a38f924e9.png" alt="Mountain landscape" />
            </div>
            <div className="mountains-overlay-content">
              <h3>Relais & Châteaux<br/>Sustainability Report</h3>
              <p>As proud members of Relais & Châteaux, we adhere to the highest standards of environmental stewardship.</p>
              <button className="link-button">
                <span>READ FULL REPORT</span>
              </button>
            </div>
          </div>

          {/* Frame 7: Iguazu Falls */}
          <div className="producing-nature-frame iguazu-frame">
            <img src="/assets/figma/e4b1294f7fdb8bb50ef6b56bc1b1317911f04905.png" alt="Iguazu Falls" />
          </div>
        </div>
      </section>

      {/* You Might Enjoy Section - Atacama specific content */}
      <section className="you-might-enjoy-section" data-scroll-section>
        <div className="you-might-enjoy-header">
          <h2>You might enjoy</h2>
        </div>

        {/* Blog Carousel - Atacama specific */}
        <div className="blog-carousel-container">
          <div className="blog-carousel" id="blog-carousel">
            {/* Card 1 - Being inspired by the United Nation's 17 Objectives for Sustainability */}
            <div className="blog-card">
              <div className="blog-card-image">
                <img src="/assets/figma/a51cb940b16e44bc99b3d02f5c428997e52243f6.png" alt="Being inspired by the United Nation's 17 Objectives for Sustainability" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Being inspired by the United Nation's 17 Objectives for Sustainability</h3>
                  <div className="blog-card-meta">
                    <span>AWASI ATACAMA</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>The Argentine-born artist and photographer employs a unique technique in her work: she applies crumpled images on top of her photographs to highlight the impact man has on nature.</p>
                <div className="blog-card-links">
                  <button className="blog-link">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Card 2 - Atacama specific */}
            <div className="blog-card">
              <div className="blog-card-image">
                <div className="image-background" style={{ backgroundImage: `url('/assets/figma/8105b348e0cf34fabbb59e590cdec5127bbf821d.png')` }}></div>
                <img src="/assets/figma/63f393221d4a609b334dda8cfefa7974091c6809.png" alt="Hospitality in harmony with the natural world" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Hospitality in harmony with the natural world</h3>
                  <div className="blog-card-meta">
                    <span>AWASI ATACAMA</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>As you watch, as you listen to the beauty of the evening, in that extraordinary quietness when almost everything seems to be motionless, you are then in complete communion, in complete harmony, with everything about you.</p>
                <div className="blog-card-links">
                  <button className="blog-link">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                  <button className="blog-link secondary">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Card 3 - Rainer Gross - "Double Takes" */}
            <div className="blog-card">
              <div className="blog-card-image">
                <div className="image-background" style={{ backgroundImage: `url('/assets/figma/8496f97aaf8ebc26c278c34dfebe8f860aa970d3.png')` }}></div>
                <img src="/assets/figma/416428dc0e386ad835266f20cec574ee10e74d7a.png" alt="Rainer Gross - Double Takes" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Rainer Gross - "Double Takes"</h3>
                  <div className="blog-card-meta">
                    <span>AWASI ATACAMA</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.</p>
                <div className="blog-card-links">
                  <button className="blog-link">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                  <button className="blog-link secondary">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Card 4 - Iliana Díaz López - "Portales del Ara Pyau" */}
            <div className="blog-card">
              <div className="blog-card-image">
                <div className="image-background" style={{ backgroundImage: `url('/assets/figma/2d65d5bae56bba760cb6fbe6511b3ef5a4b4441a.png')` }}></div>
                <img src="/assets/figma/10b5a96c07dab42db9e5f5b9b7df2db14023a972.png" alt="Iliana Díaz López - Portales del Ara Pyau" />
              </div>
              <div className="blog-card-content">
                <div className="blog-card-header">
                  <h3>Iliana Díaz López - "Portales del Ara Pyau"</h3>
                  <div className="blog-card-meta">
                    <span>AWASI ATACAMA</span>
                    <span>-</span>
                    <span>03.02.2022</span>
                  </div>
                </div>
                <p>Consectetur felis sed eget nisi lacus accumsan suscipit sem. Egestas mattis potenti pretium vulputate lectus ut interdum. Iaculis amet convallis consequat vitae dis commodo. Elementum nibh justo viverra porttitor vulputate vestibulum.</p>
                <div className="blog-card-links">
                  <button className="blog-link">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                  <button className="blog-link secondary">
                    <span>READ MORE</span>
                    <svg className="link-underline" viewBox="0 0 76 2" fill="none">
                      <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Next Arrow */}
          <button className="blog-carousel-next" aria-label="Next">
            <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
              <circle cx="29" cy="29" r="28.5" stroke="#49413d"/>
              <path d="M22 19L37 29L22 39" stroke="#49413d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </section>

      {/* Instagram Section - Atacama specific */}
      <InstagramSection
        posts={[
          { src: images.film13, alt: "Instagram 1" },
          { src: images.filmVideo1, alt: "Instagram 2", isVideo: true },
          { src: images.film14, alt: "Instagram 3" },
          { src: images.film15, alt: "Instagram 4" }
        ]}
      />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default ProducingNatureAtacama;